import React from 'react';

interface ToggleSwitchProps {
  enabled: boolean;
  onChange: () => void;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  color?: string;
  label?: string;
  labelPosition?: 'left' | 'right';
  /**
   * What this switch controls, for anyone not looking at the screen.
   *
   * The visible heading usually sits in a sibling element rather than a <label>,
   * so without this the control announces only "switch, on" — true, and useless.
   * Optional so no existing usage changes; worth passing wherever the switch is
   * not immediately self-describing.
   */
  ariaLabel?: string;
}

/**
 * Clean toggle switch - iOS style
 */
const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
  enabled,
  ariaLabel,
  onChange,
  disabled = false,
  size = 'sm',
  // Defaults to the action colour, not a raw blue. A toggle means "on" — the
  // hue carries no other meaning, so it should follow the theme rather than be
  // picked per feature. Callers may still override for a third-party brand
  // (WhatsApp green), which is the only reason to differ.
  color = 'var(--action)',
  label,
  labelPosition = 'left'
}) => {
  const handleClick = () => {
    if (!disabled) {
      onChange();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!disabled) {
        onChange();
      }
    }
  };

  // Size classes using Tailwind
  const sizeClasses = {
    sm: {
      track: 'w-9 h-5',      // 36x20
      knob: 'w-4 h-4',       // 16x16
      translate: enabled ? 'translate-x-4' : 'translate-x-0.5',
    },
    md: {
      track: 'w-11 h-6',     // 44x24
      knob: 'w-5 h-5',       // 20x20
      translate: enabled ? 'translate-x-5' : 'translate-x-0.5',
    },
    lg: {
      track: 'w-14 h-7',     // 56x28
      knob: 'w-6 h-6',       // 24x24
      translate: enabled ? 'translate-x-7' : 'translate-x-0.5',
    },
  };

  const s = sizeClasses[size];

  const toggle = (
    <button
      type="button"
      role="switch"
      aria-label={ariaLabel}
      aria-checked={enabled}
      aria-disabled={disabled}
      disabled={disabled}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={`
        relative inline-flex items-center rounded-full
        transition-colors duration-200 ease-in-out
        focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
        ${s.track}
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      `}
      // Off state was a hardcoded #CBD5E1 carrying a basil-todo; --border-default
      // is the token for the same role and resolves under whichever theme is live.
      style={{ backgroundColor: enabled ? color : 'var(--border-default)' }}
    >
      <span
        className={`
          inline-block rounded-full bg-white shadow-sm
          transform transition-transform duration-200 ease-in-out
          ${s.knob}
          ${s.translate}
        `}
      />
    </button>
  );

  if (label) {
    return (
      <label className={`inline-flex items-center gap-3 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
        {labelPosition === 'left' && <span className="text-sm font-medium text-gray-700">{label}</span>}
        {toggle}
        {labelPosition === 'right' && <span className="text-sm font-medium text-gray-700">{label}</span>}
      </label>
    );
  }

  return toggle;
};

export default ToggleSwitch;
